import { computed, ref } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import {
  normalizeWindows, currentWindows, bannerWindows, dismissalKey, pruneDismissed,
} from '@/helpers/maintenanceLogic';

// Wartungsfenster: gemeinsamer Zustand für Login-Seite, Startseite und die Meldung oben.
// Der Endpoint ist ohne Login lesbar (Login-Seite) und liefert mit Session zusätzlich Clusternamen.
const MAINTENANCE_URL = `${API_BASE_URL}/maintenance-windows`;
const POLL_MS = 60 * 1000;
const TICK_MS = 30 * 1000;
const DISMISSED_STORAGE_KEY = 'maintenance.dismissed.v1';

const rawWindows = ref([]);
export const maintenanceLoaded = ref(false);
export const maintenanceError = ref(false);

// Abweichung zwischen Server- und Browseruhr; «jetzt» ist immer die Serverzeit.
let clockOffsetMs = 0;
const nowMs = ref(Date.now());
const currentNow = () => Date.now() + clockOffsetMs;

const readDismissed = () => {
  try {
    return pruneDismissed(JSON.parse(localStorage.getItem(DISMISSED_STORAGE_KEY) || '{}'), currentNow());
  } catch (_) {
    return {};
  }
};
const dismissed = ref(readDismissed());

export const maintenanceWindows = computed(() => currentWindows(rawWindows.value, nowMs.value));
export const maintenanceBanners = computed(() => bannerWindows(rawWindows.value, dismissed.value, nowMs.value));
export const maintenanceNow = nowMs;

export const dismissMaintenance = (w) => {
  const next = pruneDismissed({ ...dismissed.value, [dismissalKey(w, currentNow())]: w.endsAt }, currentNow());
  dismissed.value = next;
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(next));
  } catch (_) {
    // Speicher gesperrt (z. B. privater Modus): Die Meldung bleibt bis zum Neuladen weggeklickt.
  }
};

let pending = null;

export const refreshMaintenance = () => {
  if (pending) return pending;
  pending = axios.get(MAINTENANCE_URL, { withCredentials: true, skipAuthLogout: true })
    .then((res) => {
      const serverTime = Date.parse(res.data?.serverTime);
      if (Number.isFinite(serverTime)) clockOffsetMs = serverTime - Date.now();
      rawWindows.value = normalizeWindows(res.data?.maintenanceWindows);
      maintenanceError.value = false;
      maintenanceLoaded.value = true;
      nowMs.value = currentNow();
    })
    .catch(() => {
      // Bei einem Fehler bleibt die letzte Liste stehen; ein Ausfall soll keine Meldung wegnehmen.
      maintenanceError.value = true;
    })
    .finally(() => { pending = null; });
  return pending;
};

let users = 0;
let pollTimer = null;
let tickTimer = null;

const onVisible = () => {
  if (document.visibilityState === 'visible') refreshMaintenance();
};

// Mehrfach aufrufbar (App und Seiten); gepollt wird, solange mindestens ein Aufrufer aktiv ist.
export const startMaintenancePolling = () => {
  users += 1;
  if (users > 1) return;
  refreshMaintenance();
  pollTimer = window.setInterval(() => { if (document.visibilityState === 'visible') refreshMaintenance(); }, POLL_MS);
  tickTimer = window.setInterval(() => { nowMs.value = currentNow(); }, TICK_MS);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
};

export const stopMaintenancePolling = () => {
  users = Math.max(0, users - 1);
  if (users > 0) return;
  window.clearInterval(pollTimer);
  window.clearInterval(tickTimer);
  pollTimer = null;
  tickTimer = null;
  document.removeEventListener('visibilitychange', onVisible);
  window.removeEventListener('focus', onVisible);
};
